import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import UserItem from "./UserItem";
import { Search as SearchIcon, X as XIcon } from "lucide-react";

const SEARCH_TABS = {
  users: "users",
  trippins: "trippins",
};

const SearchTabs = ({ activeTab, onChange }) => {
  const tabs = [
    { id: SEARCH_TABS.users, label: "Users" },
    { id: SEARCH_TABS.trippins, label: "Trippins" },
  ];

  return (
    <div className="max-w-xl mx-auto mt-4 flex justify-center">
      <div className="relative inline-flex rounded-full bg-gray-100 p-1 dark:bg-neutral-900">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={[
                "relative z-10 min-w-[110px] rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300",
                isActive
                  ? "text-black dark:text-white"
                  : "text-gray-500 dark:text-gray-300",
              ].join(" ")}
            >
              {isActive ? (
                <motion.span
                  layoutId="search-tab-pill"
                  className="absolute inset-0 rounded-full bg-white shadow-sm dark:bg-neutral-700"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              ) : null}
              <span className="relative z-10">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TrippinSearchItem = ({ trippin, trippinIds }) => {
  const openTrippin = async () => {
    const { mount } = await import("./TrippinPost/index.js");
    const domNode = document.getElementById("trippin-root");

    if (!domNode) {
      console.error("Missing trippin-root mount node.");
      return;
    }

    mount(domNode, {
      trippin_ids: trippinIds,
      selectedTrippinId: trippin.trippin_post_id,
      source: "home",
      offset: 0,
    });
  };

  return (
    <button
      type="button"
      onClick={openTrippin}
      className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-neutral-900"
    >
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-2xl bg-gray-200 dark:bg-neutral-800">
        {trippin.background_url ? (
          <img
            src={trippin.background_url}
            alt={trippin.preview_place_name || trippin.username}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-gray-500 dark:text-gray-300">
            No image
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-black dark:text-white">
          {trippin.preview_place_name || "Untitled trippin"}
        </p>
        <p className="truncate text-sm text-gray-500 dark:text-gray-300">
          @{trippin.username}
        </p>
        <p className="truncate text-xs text-gray-400 dark:text-gray-500">
          {trippin.place_count === 1 ? "1 place" : `${trippin.place_count || 0} places`}
        </p>
      </div>
    </button>
  );
};

const Search = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(SEARCH_TABS.users);
  const [searchResults, setSearchResults] = useState({
    [SEARCH_TABS.users]: [],
    [SEARCH_TABS.trippins]: [],
  });
  const [hasSearched, setHasSearched] = useState({
    [SEARCH_TABS.users]: false,
    [SEARCH_TABS.trippins]: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const requestIdRef = useRef(0);

  const performSearch = async (tab = activeTab) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    setError(null);

    try {
      const endpoint =
        tab === SEARCH_TABS.users ? "/search/users/" : "/search/trippins/";
      const response = await axiosInstance.get(`${endpoint}?query=${encodeURIComponent(trimmedQuery)}`);
      const results =
        tab === SEARCH_TABS.users ? response.data.users || [] : response.data.trippins || [];

      if (requestId !== requestIdRef.current) {
        return;
      }

      setSearchResults((prev) => ({
        ...prev,
        [tab]: results,
      }));
      setHasSearched((prev) => ({
        ...prev,
        [tab]: true,
      }));
    } catch (err) {
      console.error("Search error:", err);
      if (requestId === requestIdRef.current) {
        setError("Failed to fetch search results.");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await performSearch(activeTab);
  };

  const clearSearch = () => {
    requestIdRef.current += 1;
    setSearchQuery("");
    setLoading(false);
    setError(null);
    setSearchResults({
      [SEARCH_TABS.users]: [],
      [SEARCH_TABS.trippins]: [],
    });
    setHasSearched({
      [SEARCH_TABS.users]: false,
      [SEARCH_TABS.trippins]: false,
    });
  };

  const handleQueryChange = (e) => {
    const nextValue = e.target.value;
    setSearchQuery(nextValue);

    if (!nextValue.trim()) {
      clearSearch();
    }
  };

  const handleTabChange = async (tab) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
    setError(null);

    if (searchQuery.trim()) {
      await performSearch(tab);
    }
  };

  const activeResults = searchResults[activeTab] || [];
  const showEmptyState = !loading && !error && hasSearched[activeTab] && activeResults.length === 0;
  const trippinIds = searchResults[SEARCH_TABS.trippins].map((item) => item.trippin_post_id);

  return (
    <div className="tailwind min-h-screen bg-white dark:bg-darkBackground text-black dark:text-white pt-14 px-4 pb-4">
      <form className="max-w-xl mx-auto flex items-center gap-3" onSubmit={handleSearch}>
        <div className="relative flex-1">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <SearchIcon className="h-5 w-5 text-gray-400 dark:text-gray-300" />
          </span>
          <input
            type="text"
            className="w-full h-10 rounded-3xl bg-gray-100 dark:bg-neutral-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-300 pl-10 pr-10 focus:outline-none"
            placeholder={activeTab === SEARCH_TABS.users ? "Search users..." : "Search trippins..."}
            value={searchQuery}
            onChange={handleQueryChange}
            autoFocus
          />
          {searchQuery ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={clearSearch}
              className="absolute inset-y-0 right-2 my-auto inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-400 text-white dark:bg-neutral-700 dark:text-white"
            >
              <XIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-blue-600 dark:text-chatBlueColor text-base"
        >
          Cancel
        </button>
        {/* Hidden submit button allows Enter/Search key to submit */}
        <button type="submit" className="hidden" aria-hidden="true" />
      </form>

      <SearchTabs activeTab={activeTab} onChange={handleTabChange} />

      {error && (
        <p className="px-4 py-10 text-center text-base text-gray-500 dark:text-gray-300">{error}</p>
      )}

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${activeTab}-${loading ? "loading" : "ready"}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="max-w-xl mx-auto mt-4 divide-y divide-gray-200 dark:divide-neutral-800"
        >
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="shimmer h-12 w-12 rounded-full bg-gray-200 dark:bg-neutral-800" />
                <div className="min-w-0 flex-1">
                  <div className="shimmer h-4 w-1/3 rounded bg-gray-200 dark:bg-neutral-800 mb-2"></div>
                  <div className="shimmer h-3 w-1/4 rounded bg-gray-200 dark:bg-neutral-800"></div>
                </div>
              </div>
            ))
          ) : activeTab === SEARCH_TABS.users && activeResults.length > 0 ? (
            activeResults.map((user) => {
              const normalized = {
                user_id: user.user_id ?? user.id,
                profile_url: user.profile_url ?? user.profilePictureUrl,
                username: user.username,
                full_name: user.full_name ?? user.fullName ?? user.name,
                rating: user.rating,
              };
              const key = normalized.user_id ?? user.id ?? user.username;
              return <UserItem key={key} user={normalized} navigate={navigate} />;
            })
          ) : activeTab === SEARCH_TABS.trippins && activeResults.length > 0 ? (
            activeResults.map((trippin) => (
              <TrippinSearchItem
                key={trippin.trippin_post_id}
                trippin={trippin}
                trippinIds={trippinIds}
              />
            ))
          ) : showEmptyState ? (
            <p className="px-4 py-10 text-center text-base text-gray-500 dark:text-gray-300">No results</p>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Search;